import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Plus, Download, Upload, Target, Pencil, Trash2,
  MoreHorizontal, MapPin, ArrowUpDown, ChevronUp, ChevronDown,
  Users, Calendar, Clock, CalendarRange, Layers, Building2, ExternalLink,
} from 'lucide-react';
import { formatDateDE } from './index';
import { formatMassnahmeZeit } from './massnahmeZeit';
import { AssignVergabenDialog } from './AssignVergabenDialog';

export const MassnahmenListTab = ({
  selectedLocationId, locations, massnahmen, shiftCounts = {}, onShowShifts,
  sortedMassnahmen, massnahmenSortField, massnahmenSortDirection,
  handleMassnahmenSort, canManage,
  openNewMassnahmeDialog, openEditMassnahmeDialog, handleDeleteMassnahme,
  handleDownloadTemplate, setImportDialogOpen,
  onOpenScheduleEditor, onReload,
}) => {
  const [assignVergabenMassnahme, setAssignVergabenMassnahme] = useState(null);
  const SortHeader = ({ field, label }) => (
    <button
      type="button"
      onClick={() => handleMassnahmenSort(field)}
      className="flex items-center gap-1 font-medium hover:text-primary transition-colors"
    >
      {label}
      <ArrowUpDown className={`h-4 w-4 ${massnahmenSortField === field ? 'text-primary' : 'text-muted-foreground'}`} />
      {massnahmenSortField === field && (
        massnahmenSortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
      )}
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-lg font-semibold">
            {selectedLocationId === 'all'
              ? 'Alle Maßnahmen'
              : `Maßnahmen für ${locations.find(l => l.id === selectedLocationId)?.name || ''}`
            }
          </h2>
          <p className="text-sm text-muted-foreground">
            {massnahmen.length} Maßnahme{massnahmen.length !== 1 ? 'n' : ''} gefunden
          </p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={onOpenScheduleEditor}
              disabled={selectedLocationId === 'all' || massnahmen.length === 0}
              data-testid="open-massnahmen-schedule-btn"
            >
              <CalendarRange className="h-4 w-4 mr-2" />Wochenplan
            </Button>
            <Button variant="outline" onClick={handleDownloadTemplate}>
              <Download className="h-4 w-4 mr-2" />Vorlage
            </Button>
            <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />Import
            </Button>
            <Button onClick={openNewMassnahmeDialog} className="rounded-full" disabled={selectedLocationId === 'all'}>
              <Plus className="h-4 w-4 mr-2" />Neue Maßnahme
            </Button>
          </div>
        )}
      </div>

      {selectedLocationId === 'all' && canManage && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          Wählen Sie einen Standort aus, um eine neue Maßnahme zu erstellen.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {massnahmen.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="p-8 text-center text-muted-foreground">
              <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
              {selectedLocationId === 'all' ? (
                <>
                  <p>Noch keine Maßnahmen definiert</p>
                  <p className="text-sm">Wählen Sie einen Standort und erstellen Sie Ihre erste Maßnahme.</p>
                </>
              ) : (
                <>
                  <p>Keine Maßnahmen für diesen Standort</p>
                  <p className="text-sm">Erstellen Sie eine neue Maßnahme für {locations.find(l => l.id === selectedLocationId)?.name}.</p>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="col-span-full">
            <CardContent className="p-0">
              <div className="overflow-auto max-h-[calc(100vh-280px)]">
                <table className="w-full">
                  <thead className="bg-muted/50 sticky top-0 z-20">
                    <tr>
                      <th className="text-left p-3 shadow-[inset_0_-1px_0_0_hsl(var(--border))] bg-muted/50"><SortHeader field="name" label="Name" /></th>
                      <th className="text-left p-3 shadow-[inset_0_-1px_0_0_hsl(var(--border))] bg-muted/50"><SortHeader field="short_code" label="Kürzel" /></th>
                      <th className="text-left p-3 shadow-[inset_0_-1px_0_0_hsl(var(--border))] bg-muted/50"><SortHeader field="laufzeit" label="Laufzeit" /></th>
                      <th className="text-left p-3 shadow-[inset_0_-1px_0_0_hsl(var(--border))] bg-muted/50"><SortHeader field="teilnehmer" label="Teilnehmer" /></th>
                      <th className="text-left p-3 shadow-[inset_0_-1px_0_0_hsl(var(--border))] bg-muted/50"><SortHeader field="uhrzeit" label="Uhrzeit" /></th>
                      <th className="text-left p-3 font-medium shadow-[inset_0_-1px_0_0_hsl(var(--border))] bg-muted/50">Standort</th>
                      <th className="text-left p-3 font-medium shadow-[inset_0_-1px_0_0_hsl(var(--border))] bg-muted/50">Status</th>
                      <th className="text-left p-3 font-medium shadow-[inset_0_-1px_0_0_hsl(var(--border))] bg-muted/50">Einsätze</th>
                      <th className="text-left p-3 font-medium shadow-[inset_0_-1px_0_0_hsl(var(--border))] bg-muted/50">Vergaben</th>
                      {canManage && <th className="text-right p-3 font-medium w-24 shadow-[inset_0_-1px_0_0_hsl(var(--border))] bg-muted/50">Aktionen</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMassnahmen.map(massnahme => {
                      const shiftCount = shiftCounts[massnahme.id] || 0;
                      return (
                        <tr key={massnahme.id} className="border-b hover:bg-muted/30 transition-colors">
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              <div className="w-3 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: massnahme.color }} />
                              <div>
                                <p className="font-medium">{massnahme.name}</p>
                                {massnahme.description && (
                                  <p className="text-xs text-muted-foreground line-clamp-1">{massnahme.description}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="font-mono" style={{ borderColor: massnahme.color, color: massnahme.color }}>
                              {massnahme.short_code}
                            </Badge>
                          </td>
                          <td className="p-3">
                            {(massnahme.start_date || massnahme.end_date) ? (
                              <div className="flex items-center gap-1 text-sm">
                                <Calendar className="h-3 w-3 text-muted-foreground" />
                                {massnahme.start_date && massnahme.end_date
                                  ? `${formatDateDE(massnahme.start_date)} - ${formatDateDE(massnahme.end_date)}`
                                  : massnahme.start_date
                                    ? `Ab ${formatDateDE(massnahme.start_date)}`
                                    : `Bis ${formatDateDE(massnahme.end_date)}`
                                }
                              </div>
                            ) : <span className="text-muted-foreground text-sm">—</span>}
                            {massnahme.excluded_weeks?.length > 0 && (
                              <p className="text-xs text-amber-600 mt-1">{massnahme.excluded_weeks.length} Woche(n) ausgeschlossen</p>
                            )}
                            {massnahme.scheduled_weeks?.length > 0 && (
                              <p className="text-xs text-primary mt-1">Nur in {massnahme.scheduled_weeks.length} KW geplant</p>
                            )}
                          </td>
                          <td className="p-3">
                            {massnahme.teilnehmer_anzahl ? (
                              <Badge variant="outline" className="bg-primary/5">
                                <Users className="h-3 w-3 mr-1" />{massnahme.teilnehmer_anzahl}
                              </Badge>
                            ) : <span className="text-muted-foreground text-sm">—</span>}
                          </td>
                          <td className="p-3" data-testid={`massnahme-zeit-${massnahme.id}`}>
                            {(() => {
                              const z = formatMassnahmeZeit(massnahme);
                              if (z.isEmpty) {
                                return (
                                  <span
                                    className="text-muted-foreground text-sm"
                                    title="Keine Durchführungszeit hinterlegt — Export zählt mit 8 Std. (08:00–16:00)"
                                  >
                                    — <span className="text-xs">(8h Standard)</span>
                                  </span>
                                );
                              }
                              return (
                                <div className="flex flex-wrap items-center gap-1">
                                  <Clock className="h-3 w-3 text-muted-foreground" />
                                  {z.badges.map((b, i) => (
                                    <Badge
                                      key={i}
                                      variant="outline"
                                      className="text-xs font-normal py-0.5"
                                      title={`${b.label}: ${b.time} Uhr`}
                                    >
                                      <span className="font-medium mr-1">{b.label}</span>
                                      {b.time}
                                    </Badge>
                                  ))}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="p-3">
                            {massnahme.location_name ? (
                              <div className="flex items-center gap-1 text-sm">
                                <MapPin className="h-3 w-3 text-muted-foreground" />{massnahme.location_name}
                              </div>
                            ) : (
                              <Badge
                                variant="outline"
                                className="text-indigo-700 border-indigo-300 bg-indigo-50"
                                title="Standortübergreifende Maßnahme — in allen Standorten sichtbar"
                                data-testid={`massnahme-firmenweit-${massnahme.id}`}
                              >
                                <Building2 className="h-3 w-3 mr-1" />Firmenweit
                              </Badge>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex flex-wrap items-center gap-1">
                              <Badge variant={massnahme.is_active ? 'default' : 'secondary'}>
                                {massnahme.is_active ? 'Aktiv' : 'Inaktiv'}
                              </Badge>
                              {massnahme.hide_in_tv && (
                                <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50" title="In TV-Ansicht ausgeblendet">
                                  TV verborgen
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            {shiftCount > 0 && onShowShifts ? (
                              <button
                                type="button"
                                onClick={() => onShowShifts(massnahme)}
                                title="Einsätze dieser Maßnahme im Einsatzplan anzeigen"
                                data-testid={`massnahme-shifts-link-${massnahme.id}`}
                                className="inline-flex"
                              >
                                <Badge variant="outline" className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors gap-1">
                                  {shiftCount}
                                  <ExternalLink className="h-3 w-3" />
                                </Badge>
                              </button>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground">{shiftCount}</Badge>
                            )}
                          </td>
                          <td className="p-3">
                            {canManage ? (
                              <button
                                type="button"
                                onClick={() => setAssignVergabenMassnahme(massnahme)}
                                className="inline-flex items-center gap-1 hover:opacity-80 transition-opacity"
                                title="Vergaben zuordnen"
                                data-testid={`massnahme-vergaben-count-${massnahme.id}`}
                              >
                                <Badge variant={(massnahme.parent_measure_ids?.length || 0) > 0 ? 'secondary' : 'outline'} className="text-muted-foreground">
                                  <Layers className="h-3 w-3 mr-1" />{massnahme.parent_measure_ids?.length || 0}
                                </Badge>
                              </button>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground">
                                <Layers className="h-3 w-3 mr-1" />{massnahme.parent_measure_ids?.length || 0}
                              </Badge>
                            )}
                          </td>
                          {canManage && (
                            <td className="p-3 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openEditMassnahmeDialog(massnahme)}>
                                    <Pencil className="h-4 w-4 mr-2" />Bearbeiten
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => setAssignVergabenMassnahme(massnahme)} data-testid={`assign-vergaben-menu-${massnahme.id}`}>
                                    <Layers className="h-4 w-4 mr-2" />Vergaben zuordnen
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleDeleteMassnahme(massnahme)} className="text-destructive">
                                    <Trash2 className="h-4 w-4 mr-2" />Löschen
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <AssignVergabenDialog
        massnahme={assignVergabenMassnahme}
        onClose={() => setAssignVergabenMassnahme(null)}
        onSaved={onReload}
      />
    </div>
  );
};
